import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from './en.json';
import es from './es.json';

const DICTS = { en, es };
const STORAGE_KEY = 'cc_lang_v1';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key, fallback) => fallback || key,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'es') return stored;
    } catch (_e) {
      // ignore
    }
    // Detect browser language for first-time visitors
    const browserLang = (navigator.language || 'en').toLowerCase();
    return browserLang.startsWith('es') ? 'es' : 'en';
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch (_e) {
      // ignore
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next === 'en' || next === 'es') setLangState(next);
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const dict = DICTS[lang] || DICTS.en;
      if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
      // Fallback to English if Spanish key is missing
      if (Object.prototype.hasOwnProperty.call(DICTS.en, key)) return DICTS.en[key];
      return fallback != null ? fallback : key;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
